import { Link, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { useRegister } from '@/data/auth';
import { useDocumentTitle } from '@/lib/useDocumentTitle';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';

const schema = z.object({
  firstname: z.string().min(1, 'Vorname eingeben.'),
  lastname:  z.string().min(1, 'Nachname eingeben.'),
  email:     z.string().email('Bitte gültige E-Mail.'),
  password:  z.string().min(8, 'Mindestens 8 Zeichen.'),
});
type FormValues = z.infer<typeof schema>;

export function RegisterPage() {
  useDocumentTitle('Registrieren');
  const navigate = useNavigate();
  const register = useRegister();

  const {
    register: rhfRegister,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const onSubmit = async (values: FormValues) => {
    try {
      await register.mutateAsync(values);
      toast.success('Konto erstellt – bitte E-Mail bestätigen.');
      void navigate({ to: '/login' });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Registrierung fehlgeschlagen');
    }
  };

  return (
    <>
      <h1 className="text-2xl font-semibold">Konto erstellen</h1>
      <p className="mt-1 text-sm text-[var(--muted-foreground)]">
        Starte dein digitales Tauchlogbuch.
      </p>

      <form className="mt-6 space-y-4" onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="firstname">Vorname</Label>
            <Input id="firstname" autoComplete="given-name" {...rhfRegister('firstname')} />
            {errors.firstname && <p className="text-xs text-[var(--destructive)]">{errors.firstname.message}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="lastname">Nachname</Label>
            <Input id="lastname" autoComplete="family-name" {...rhfRegister('lastname')} />
            {errors.lastname && <p className="text-xs text-[var(--destructive)]">{errors.lastname.message}</p>}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="email">E-Mail</Label>
          <Input id="email" type="email" autoComplete="email" {...rhfRegister('email')} />
          {errors.email && <p className="text-xs text-[var(--destructive)]">{errors.email.message}</p>}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="password">Passwort</Label>
          <Input id="password" type="password" autoComplete="new-password" {...rhfRegister('password')} />
          {errors.password && <p className="text-xs text-[var(--destructive)]">{errors.password.message}</p>}
        </div>

        <Button type="submit" className="w-full" disabled={register.isPending}>
          {register.isPending ? <Spinner /> : 'Konto erstellen'}
        </Button>
      </form>

      <p className="mt-6 text-center text-sm text-[var(--muted-foreground)]">
        Schon ein Konto?{' '}
        <Link to="/login" className="font-medium text-ocean-700 hover:underline">
          Hier einloggen
        </Link>
      </p>
    </>
  );
}
